/**
 * SystemLog Component
 *
 * Displays a chronological log of admin/system actions.
 * Compact list view — click an entry to open a slide-over detail panel.
 * Backed by DynamoDB via GraphQL for persistent audit logging.
 */

import { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, Trash2, Shield, User, Undo2, Activity, Tag } from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { listSystemLogEntries } from '@/graphql/queries';
import { createSystemLogEntry, deleteSystemLogEntry } from '@/graphql/mutations';
import CrownLoader from '@/components/ui/CrownLoader';
import { DataTable } from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';

const client = generateClient();

export interface SystemLogEntry {
  id: string;
  timestamp: string;
  action: 'DELETE_APPLICATION' | 'DELETE_PARTICIPANT' | 'APPROVE' | 'REJECT' | 'LOCK_PROFILE' | 'RETURN_APPLICATION';
  performedBy: string;
  targetName: string;
  targetEmail: string;
  targetId: string;
  details?: string;
}

/**
 * Fetch all system log entries from DynamoDB via GraphQL.
 * Returns entries sorted by timestamp descending (newest first).
 */
export async function getSystemLog(): Promise<SystemLogEntry[]> {
  try {
    const result = await client.graphql({
      query: listSystemLogEntries,
      variables: { limit: 500 },
      authMode: 'userPool',
    }) as GraphQLResult<{ listSystemLogEntries: SystemLogEntry[] }>;
    const items = result.data?.listSystemLogEntries || [];
    // Sort by timestamp descending since DynamoDB Scan doesn't guarantee order
    return items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  } catch (err) {
    console.error('[SystemLog] Failed to fetch from DynamoDB:', err);
    return [];
  }
}

/**
 * Log a system action to DynamoDB via GraphQL mutation.
 * Fire-and-forget — does not await the result.
 */
export function logSystemAction(entry: Omit<SystemLogEntry, 'id' | 'timestamp'>): void {
  const promise = client.graphql({
    query: createSystemLogEntry,
    variables: {
      input: {
        action: entry.action,
        performedBy: entry.performedBy,
        targetName: entry.targetName,
        targetEmail: entry.targetEmail,
        targetId: entry.targetId,
        details: entry.details || '',
      },
    },
    authMode: 'userPool',
  }) as Promise<GraphQLResult<{ createSystemLogEntry: SystemLogEntry }>>;
  promise.then(() => {
    console.log('[SystemLog] Logged action:', entry.action, entry.targetName);
  }).catch((err: unknown) => {
    console.error('[SystemLog] Failed to write to DynamoDB:', err);
  });
}

/**
 * Delete a system log entry from DynamoDB via GraphQL mutation.
 */
export async function deleteSystemLogEntryById(id: string): Promise<void> {
  try {
    await client.graphql({
      query: deleteSystemLogEntry,
      variables: { id },
      authMode: 'userPool',
    });
  } catch (err) {
    console.error('[SystemLog] Failed to delete entry:', err);
  }
}

const ACTION_LABELS: Record<SystemLogEntry['action'], string> = {
  DELETE_APPLICATION: 'Deleted Application',
  DELETE_PARTICIPANT: 'Deleted Contestant',
  APPROVE: 'Approved',
  REJECT: 'Rejected',
  LOCK_PROFILE: 'Locked Profile',
  RETURN_APPLICATION: 'Returned to Applicant',
};

const ACTION_BADGE_COLORS: Record<SystemLogEntry['action'], string> = {
  DELETE_APPLICATION: 'bg-destructive-muted text-destructive-text',
  DELETE_PARTICIPANT: 'bg-warning-muted text-warning-text',
  APPROVE: 'bg-success-muted text-success-text',
  REJECT: 'bg-destructive-muted text-destructive-text',
  LOCK_PROFILE: 'bg-info-muted text-info-text',
  RETURN_APPLICATION: 'bg-warning-muted text-warning-text',
};

function ActionIcon({ action }: { action: SystemLogEntry['action'] }) {
  const cls = "w-4 h-4";
  switch (action) {
    case 'APPROVE': return <CheckCircle className={cls} />;
    case 'REJECT': return <XCircle className={cls} />;
    case 'DELETE_APPLICATION':
    case 'DELETE_PARTICIPANT': return <Trash2 className={cls} />;
    case 'LOCK_PROFILE': return <Shield className={cls} />;
    case 'RETURN_APPLICATION': return <Undo2 className={cls} />;
  }
}

export function SystemLog() {
  const [entries, setEntries] = useState<SystemLogEntry[]>([]);
  const [filter, setFilter] = useState<SystemLogEntry['action'] | 'ALL'>('ALL');
  const [selected, setSelected] = useState<SystemLogEntry | null>(null);
  const [undoTarget, setUndoTarget] = useState<SystemLogEntry | null>(null);
  const [undoProcessing, setUndoProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchEntries = async () => {
    setLoading(true);
    const items = await getSystemLog();
    setEntries(items);
    setLoading(false);
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const filtered = filter === 'ALL' ? entries : entries.filter(e => e.action === filter);

  const handleUndo = async () => {
    if (!undoTarget) return;
    setUndoProcessing(true);
    try {
      // Log the undo action
      logSystemAction({ action: 'RETURN_APPLICATION', performedBy: 'Admin', targetName: undoTarget.targetName, targetEmail: undoTarget.targetEmail, targetId: undoTarget.targetId, details: `Undo: ${ACTION_LABELS[undoTarget.action]} — reversed by admin` });
      // Remove original entry from DynamoDB
      await deleteSystemLogEntryById(undoTarget.id);
      // Refresh entries
      await fetchEntries();
      setUndoTarget(null);
    } catch {} finally { setUndoProcessing(false); }
  };

  return (
    <div>
      <PageHeader
        size="sm"
        className="mb-6"
        action={
          <button
            onClick={() => fetchEntries()}
            className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer rounded-input"
          >
            Refresh
          </button>
        }
      >
        System Log
      </PageHeader>

      {entries.length > 0 && (
        <div className="flex gap-2 mb-5 flex-wrap">
          {(['ALL', 'APPROVE', 'REJECT', 'DELETE_APPLICATION', 'LOCK_PROFILE'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`pill-shape touch-target transition-colors ${
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {f === 'ALL' ? 'All' : ACTION_LABELS[f]}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="rounded-card border border-border bg-card shadow-subtle p-12 flex justify-center">
          <CrownLoader size={48} message="Loading system log…" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-card border border-border bg-card shadow-subtle p-12 text-center text-muted-foreground font-body">
          {entries.length === 0 ? 'No system actions recorded yet.' : 'No matching entries.'}
        </div>
      ) : (
        <DataTable
          ariaLabel="System actions"
          rowKey={(entry) => entry.id}
          rows={filtered}
          minWidth={720}
          onRowClick={(entry) => setSelected(entry)}
          columns={[
            {
              id: 'action',
              header: (
                <span className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold uppercase tracking-[0.2em]">
                  <Activity className="w-3 h-3 text-muted-foreground" />
                  Action
                </span>
              ),
              width: '64px',
              renderCell: (entry) => (
                <div className={`inline-flex items-center justify-center p-2 rounded ${ACTION_BADGE_COLORS[entry.action]}`}>
                  <ActionIcon action={entry.action} />
                </div>
              ),
            },
            {
              id: 'target',
              header: (
                <span className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold uppercase tracking-[0.2em]">
                  <User className="w-3 h-3 text-muted-foreground" />
                  Target
                </span>
              ),
              renderCell: (entry) => (
                <div className="min-w-0">
                  <p className="font-sans text-sm font-medium text-foreground truncate">
                    {ACTION_LABELS[entry.action]} — {entry.targetName}
                  </p>
                  <p className="text-eyebrow-sm text-muted-foreground mt-0.5">
                    {entry.performedBy} · {new Date(entry.timestamp).toLocaleDateString()}
                  </p>
                </div>
              ),
            },
            {
              id: 'badge',
              header: (
                <span className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold uppercase tracking-[0.2em]">
                  <Tag className="w-3 h-3 text-muted-foreground" />
                  Type
                </span>
              ),
              hideOnMobile: true,
              renderCell: (entry) => (
                <span className={`pill-shape ${ACTION_BADGE_COLORS[entry.action]}`}>
                  {entry.action.replace('_', ' ')}
                </span>
              ),
            },
            {
              id: 'undo',
              header: '',
              align: 'right',
              width: '56px',
              renderCell: (entry) => (
                <button
                  onClick={(e) => { e.stopPropagation(); setUndoTarget(entry); }}
                  className="p-2 touch-target bg-warning-muted text-warning-text hover:bg-warning hover:text-warning-foreground transition-all rounded-input border-none cursor-pointer"
                  title="Undo this action"
                  aria-label="Undo this action"
                >
                  <Undo2 className="w-4 h-4" />
                </button>
              ),
            },
          ]}
        />
      )}

      {/* Undo Confirmation Dialog */}
      {undoTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setUndoTarget(null)} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <h3 className="font-display text-2xl italic text-foreground mb-4">Undo Action</h3>
            <p className="font-body text-sm text-foreground mb-2">Are you sure you want to undo this action?</p>
            <div className="bg-muted p-4 border border-border mb-6">
              <p className="text-eyebrow-sm text-muted-foreground mb-1">Action</p>
              <p className="font-body text-sm text-foreground font-bold">{ACTION_LABELS[undoTarget.action]}</p>
              <p className="text-eyebrow-sm text-muted-foreground mt-3 mb-1">Target</p>
              <p className="font-body text-sm text-foreground">{undoTarget.targetName} ({undoTarget.targetEmail})</p>
              <p className="text-eyebrow-sm text-muted-foreground mt-3 mb-1">Date</p>
              <p className="font-body text-sm text-foreground">{new Date(undoTarget.timestamp).toLocaleString()}</p>
            </div>
            <div className="flex gap-4">
              <button onClick={handleUndo} disabled={undoProcessing} className="flex-grow flex items-center justify-center gap-2 bg-warning text-warning-foreground py-3 min-h-[var(--touch-target-min)] text-eyebrow-sm font-bold hover:bg-warning/95 transition-colors disabled:opacity-50 border-none cursor-pointer">
                <Undo2 className="w-4 h-4" /> {undoProcessing ? 'Processing…' : 'Confirm Undo'}
              </button>
              <button onClick={() => setUndoTarget(null)} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Slide-over */}
      {selected && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-card h-full shadow-2xl flex flex-col">
            <div className="p-8 border-b border-border flex items-center justify-between bg-muted/50">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded ${ACTION_BADGE_COLORS[selected.action]}`}>
                  <ActionIcon action={selected.action} />
                </div>
                <div>
                  <h2 className="font-display text-2xl not-italic text-foreground">{ACTION_LABELS[selected.action]}</h2>
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-muted-foreground">System Event</span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer">
                <X className="w-6 h-6 text-muted-foreground" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-8 space-y-8">
              <section className="space-y-4">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2">Target</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-eyebrow-sm text-muted-foreground">Name</p>
                    <p className="font-body text-sm text-foreground">{selected.targetName}</p>
                  </div>
                  <div>
                    <p className="text-eyebrow-sm text-muted-foreground">Email</p>
                    <p className="font-body text-sm text-foreground">{selected.targetEmail || '—'}</p>
                  </div>
                  <div>
                    <p className="text-eyebrow-sm text-muted-foreground">ID</p>
                    <p className="font-body text-xs text-muted-foreground font-mono">{selected.targetId}</p>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2">Action Details</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-eyebrow-sm text-muted-foreground">Action</p>
                    <p className="font-body text-sm text-foreground">{ACTION_LABELS[selected.action]}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-eyebrow-sm text-muted-foreground">Performed By</p>
                      <p className="font-body text-sm text-foreground">{selected.performedBy}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-eyebrow-sm text-muted-foreground">Timestamp</p>
                    <p className="font-body text-sm text-foreground">{new Date(selected.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              </section>

              {selected.details && (
                <section className="space-y-4">
                  <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2">Notes</h3>
                  <div className="bg-muted p-4 border border-border">
                    <p className="font-body text-sm text-muted-foreground italic leading-relaxed">{selected.details}</p>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemLog;
